import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  QuestionTypeV2,
  PartialBy,
  QuestionMultiChoiceWithText,
  GroundTruthMultiChoiceWithText,
} from '../../redux/types';
import ColorContext from '../../context/ColorContext';
import GTResetButton from './GTResetButton';

type Props = {
  groundTruthId?: number;
  surveyId: number;
  sectionId: number;
  questionId: number;
  caseId: number;
  question: QuestionMultiChoiceWithText;
  payload?: GroundTruthMultiChoiceWithText['payload'];
  gtGroupId: number|null;
  onSave: (gtData: PartialBy<GroundTruthMultiChoiceWithText, 'id'>) => void;
  onCancel: () => void;
  onDelete: (dgtId: number) => void;
};

const getInitalPayload = (
  question: QuestionMultiChoiceWithText,
  strict: boolean,
): GroundTruthMultiChoiceWithText['payload'] | undefined => {
  const initPayload = question.payload?.reduce((acc, cur) => {
    acc.truths = {
      ...acc.truths,
      [cur.id]: {
        score: 0,
      },
    };
    acc.strict = strict;
    acc.score = 0;
    return acc;
  }, {} as GroundTruthMultiChoiceWithText['payload']);
  return initPayload;
};

const GTMultiChoiceWithTextEditor: React.FC<Props> = ({
  groundTruthId,
  surveyId,
  sectionId,
  questionId,
  caseId,
  question,
  payload,
  gtGroupId,
  onSave,
  onCancel,
  onDelete,
}: Props) => {
  const color = useContext(ColorContext);
  const [payloadBuffer, setpayloadBuffer] = useState<GroundTruthMultiChoiceWithText['payload'] | undefined>(payload || getInitalPayload(question, false));
  const [strict, setStrict] = useState<boolean>(payload?.strict || false);

  const saveHandler = () => {
    if (!payloadBuffer) {
      return;
    }
    const newGTRecord: PartialBy<GroundTruthMultiChoiceWithText, 'id'> = {
      id: groundTruthId,
      digitalSurveyId: surveyId,
      digitalSectionId: sectionId,
      digitalQuestionId: questionId,
      digitalCaseId: caseId,
      type: QuestionTypeV2.MultiChoiceWithText,
      payload: payloadBuffer,
      digitalGroundTruthGroupId: gtGroupId,
    };
    onSave(newGTRecord);
  };

  const cancelHandler = () => {
    onCancel();
  };

  const onOptionScoreChangeRelax = (
    event: React.ChangeEvent<HTMLInputElement>,
    optionId: number,
  ) => {
    if (payloadBuffer?.strict === true) {
      return;
    }
    const score = parseInt(event.target.value, 10);
    setpayloadBuffer({
      ...payloadBuffer,
      truths: {
        ...payloadBuffer?.truths,
        [optionId]: {
          score,
        },
      },
      strict: false,
      score: 0,
    } as GroundTruthMultiChoiceWithText['payload']);
  };

  const handleStrictChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const strictMode = event.target.checked;
    setStrict(strictMode);
    if (strictMode) {
      setpayloadBuffer(getInitalPayload(question, true));
    } else {
      setpayloadBuffer(getInitalPayload(question, false));
    }
  };

  const handleTruthChange = (event: React.ChangeEvent<HTMLInputElement>, optionId: number) => {
    const check = event.target.checked;
    if (payloadBuffer?.strict) {
      setpayloadBuffer({
        ...payloadBuffer,
        truths: {
          ...payloadBuffer.truths,
          [optionId]: {
            score: check ? 1 : 0,
          },
        },
        strict: true,
      });
    }
  };

  const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const score = Number(e.target.value);
    if (payloadBuffer?.strict) {
      setpayloadBuffer({
        ...payloadBuffer,
        score,
      });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end">
        <FormControlLabel
          control={
            <Checkbox onChange={handleStrictChange} checked={strict} />
          }
          label="Strict Mode"
        />
      </Box>
      <Box sx={{ mx: 2, mb: 3 }}>
        {
          payloadBuffer && !payloadBuffer.strict && (
            <Box>
              <Typography color="textSecondary">{question.text}</Typography>
              {
                question.payload?.map((op) => (
                  <Box display="flex" alignItems="flex-end" gap={3} key={op.id} sx={{ my: 1 }}>
                    <Typography gutterBottom component="p" sx={{ flexBasis: 500 }}>{op.text}</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={payloadBuffer.truths?.[op.id]?.score || 0}
                      onChange={(
                        e: React.ChangeEvent<HTMLInputElement>,
                      ) => onOptionScoreChangeRelax(e, op.id)}
                    />
                  </Box>
                ))
              }
            </Box>
          )
        }
        {
          payloadBuffer && payloadBuffer.strict && (
            <Box>
              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend">{question.text}</FormLabel>
                <FormGroup>
                  {
                    question.payload?.map((op) => (
                      <FormControlLabel
                        key={op.id}
                        control={(
                          <Checkbox
                            checked={payloadBuffer.truths[op.id]?.score > 0}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => { handleTruthChange(e, op.id); }}
                          />
                        )}
                        label={op.text}
                      />
                    ))
                  }
                </FormGroup>
              </FormControl>
              <Box sx={{ my: 2 }}>
                <TextField
                  type="number"
                  size="small"
                  label="Score"
                  value={payloadBuffer.score}
                  onChange={handleScoreChange}
                />
              </Box>
            </Box>
          )
        }
      </Box>
      <Divider />
      <Box display="flex" sx={{ mt: 1 }} justifyContent="flex-end" gap={1}>
        <Box flexGrow={1}>
          <GTResetButton groundTruthId={groundTruthId} onReset={onDelete} />
        </Box>
        <Button variant="contained" color={color} onClick={saveHandler}><FormattedMessage id="save" /></Button>
        <Button variant="contained" color="secondary" onClick={cancelHandler}><FormattedMessage id="genericCancel" /></Button>
      </Box>
    </Box>
  );
};

export default GTMultiChoiceWithTextEditor;
